import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import {
  ChartAllModule,
  IAccLoadedEventArgs,
  AccumulationChartAllModule,
} from '@syncfusion/ej2-angular-charts';
import { Browser } from '@syncfusion/ej2-base';

interface ChartDataItem {
  x: string;
  y: number;
  text: string;
  fill?: string;
}

@Component({
  selector: 'app-pie',
  templateUrl: 'pie.component.html',
  styleUrls: ['pie.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [ChartAllModule, AccumulationChartAllModule, TranslateModule],
})
export class PieComponent implements OnInit, OnChanges {
  @Input() chartData: ChartDataItem[] = [];
  @Input() showLegend: boolean = false;
  @Input() innerRadiusPercentage: string = '';

  public data: Object[] = [];
  public colorPalettes: string[] = ['#14D520', '#d50b3e', '#ff9800', '#9e9e9e'];

  // Legend settings
  public legendSettings: Object = {
    visible: false,
    toggleVisibility: false,
    position: 'Bottom',
    maximumColumns: Browser.isDevice ? 2 : 3,
    fixedWidth: true,
  };

  public innerRadius: string = '';
  public startAngle: number = 70;
  public radius: string = Browser.isDevice ? '80%' : '85%';

  // Datalabel
  public dataLabel: Object = {
    name: 'text',
    visible: true,
    font: {
      fontWeight: '600',
      color: '#ffffff',
      size: '14px',
    },
  };

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.initializeChart();

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadChartData();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData']) {
      // ✅ FIXED: Load data whenever chartData changes, including first change
      this.loadChartData();
    }
    if (changes['showLegend']) {
      this.legendSettings = {
        ...this.legendSettings,
        visible: this.showLegend,
      };
    }
  }

  private initializeChart(): void {
    // Set inner radius
    if (this.innerRadiusPercentage) {
      this.innerRadius = this.innerRadiusPercentage;
    } else {
      this.innerRadius = window.innerWidth <= 768 ? '80%' : '40%';
    }

    this.loadChartData();
  }

  private loadChartData(): void {
    // ✅ FIXED: Properly handle empty or invalid chart data
    if (this.chartData && this.chartData.length > 0) {
      // Use provided chart data
      this.data = this.chartData.map((item) => ({
        x: item.x,
        y: item.y,
        text: item.text,
        fill: item.fill,
      }));

      console.log('📊 Chart data loaded:', this.data);
    } else {
      // ✅ OPTION 1: Show empty state (recommended for test results)
      this.data = [];
    }
  }

  public load(args: IAccLoadedEventArgs): void {
    const theme = 'Material';
    args.chart.theme = theme as any;
  }

  public tooltip: Object = {
    enable: true,
    format: '<b>${point.x}</b> : <b>${point.y}</b>',
    header: '',
    enableHighlight: true,
  };

  public onPointRender(args: any): void {
    // Apply border styles
    if (args.point.index % 2 !== 0) {
      args.border = { color: 'var(--cpurple10)', width: 12 };
    } else {
      args.border = { color: 'transparent', width: 4 };
    }

    // ✅ FIXED: Override fill from data object
    if (args.point?.fill) {
      args.fill = args.point.fill;
    }
  }
}
